/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import mbpLogger from '@module-logger/mbp';
import QueryString from 'qs';
import isProductListingAd from '../../isProductListingAd';

export const sumValues = (values = []) => {
    const safeValues = Array.isArray(values) ? values : [];
    const fallbackSum = () => {
        try {
            return (safeValues || []).reduce((sum, value) => sum + (Number(value) || 0), 0);
        } catch (ex) {
            let sum = 0;

            for (let index = 0; index < (safeValues || []).length; index += 1) {
                sum += Number(safeValues[index]) || 0;
            }

            return sum;
        }
    };

    try {
        if (typeof Math !== 'undefined' && typeof Math.sumPrecise === 'function') {
            return Math.sumPrecise(safeValues);
        }
        return fallbackSum();
    } catch (ex) {
        mbpLogger.logError({
            function: 'sumValues',
            appName: process.env.npm_package_name,
            module: 'mbp-page',
            jsError: ex,
            message: 'Failed to sumValues.',
        });
        return fallbackSum();
    }
};

export const getBaseCode = (productCode, skuWithAlpha = false) => {
    try {
        if (productCode) {
            let baseCode = productCode;
            if (productCode.indexOf('_') !== -1) {
                const productCodeArr = productCode.split('_');
                if (productCodeArr.length > 2) {
                    [, , baseCode] = productCodeArr;
                } else if (productCodeArr.length <= 2) {
                    [, baseCode] = productCodeArr;
                }
            } else if (productCode.indexOf('-') !== -1) {
                const productCodeArr = productCode.split('-');
                if (productCodeArr.length > 2) {
                    // if product id is 1019-I-6X, it will return 6X
                    [, , baseCode] = productCodeArr;
                } else if (productCodeArr.length <= 2) {
                    // if product id is 1002-P153345, it will return P153345
                    [, baseCode] = productCodeArr;
                }
            }

            // if string has numeric digit
            if (/\d/.test(baseCode) && baseCode.replace(/[A-Za-z]*$/, '') !== '' && !skuWithAlpha) {
                return baseCode.replace(/[A-Za-z]*$/, '');
            }
            // return SYFILETTRIM for 1018-P-SYFILETTRIM sku
            return baseCode;
        }
    } catch (ex) {
        mbpLogger.logError({
            productCode,
            function: 'getBaseCode',
            appName: process.env.npm_package_name,
            module: 'mbp-page',
            jsError: ex,
            message: 'Failed to get product base code.',
        });
    }

    return null;
};

export const getBrandId = (productCode) => {
    try {
        if (productCode) {
            if (productCode.substring(0, productCode.indexOf('-'))) {
                return productCode.substring(0, productCode.indexOf('-'));
            }

            return productCode.substring(0, productCode.indexOf('_'));
        }
    } catch (ex) {
        mbpLogger.logError({
            productCode,
            function: 'getBrand',
            appName: process.env.npm_package_name,
            module: 'tracking',
            jsError: ex,
            message: 'Error getting product brand.',
        });
    }

    return null;
};

export const getPrice = (product) => {
    let prices = null;

    if (product) {
        // Category/Search
        if (product.skuPriceRange) {
            prices = product.skuPriceRange;

            if (prices) {
                const { sale } = prices;

                // Return minimum retail price
                if (sale && sale.length) {
                    return `${sale[0].total || sale[0].value}`;
                }
            }
        }

        // PDP
        if (product?.prices) {
            const defaultSkuIndex = (product.productSkus || []).find?.((sku) => sku?.isDefaultSku);
            let toFilterSku = defaultSkuIndex?.id || product.productSkus?.[0]?.id;
            if (typeof window !== 'undefined' && window?.location?.search) {
                const urlParams = QueryString.parse(window?.location?.search || '');

                const isPla = isProductListingAd(window?.location?.search || '');

                if (isPla || window?.location?.search?.includes('sku')) {
                    const partNumber = urlParams?.['?adid'] || urlParams?.adid || urlParams?.['?sku'] || urlParams?.sku;
                    toFilterSku = partNumber;
                }
            }

            const priceOfProduct = (product?.prices || []).find((item) => item.skuPartNumber === toFilterSku && item.type === 'sale');
            // pull price
            return priceOfProduct?.total || priceOfProduct?.value;
        }
        if (product.productSkus) {
            const defaultSkuIndex = (product.productSkus || []).find?.((sku) => sku?.isDefaultSku);
            const sku = defaultSkuIndex?.id ? defaultSkuIndex : product?.productSkus?.[0];
            prices = sku?.prices || null;
            if (prices) {
                const [retail, sale] = prices;
                return `${sale?.total || sale?.value || retail?.total || retail?.value}`;
            }
        }

        // Add Ons
        if (product.prices && product.prices.length) {
            if (product.prices.length > 1) { // Both retail and sales fields present
                const [sale, retail] = product.prices;

                // Not on sale
                if (sale.value === retail.value) {
                    return `${retail.total || retail.value}`;
                }

                // Sale
                if (sale.value !== retail.value) {
                    return `${sale.total || sale.value}`;
                }
            }

            // Retail
            return `${product.prices[0].total || product.prices[0].value}`;
        }

        // Checkout
        if (product.price) {
            const getCollectedDiscount = (product.itemDiscounts?.promotions || []).map((item) => Math.abs(Number(item?.discountAmount)));
            const collectedDiscount = sumValues(getCollectedDiscount);
            if (collectedDiscount) {
                return Number(parseFloat(product.price - collectedDiscount / Number(product.quantity || 1)).toFixed(2));
            }
            return product.price;
        }
    }

    return null;
};

export const isValidProduct = (product) => {
    const REQUIRED_FIELDS = [
        'partNumber',
        'name',
    ];
    let valid = true;

    REQUIRED_FIELDS.forEach(
        (field) => {
            if (!product[field]) {
                valid = false;
            }
        },
    );

    return valid;
};

export const productIdWithBaseCode = (products) => {
    if (products?.length) {
        const allProducts = [];
        (products || []).forEach((item) => {
            const productItem = item;
            if (productItem.partNumber) {
                productItem.partNumber = getBaseCode(productItem.partNumber);
            }
            const pSkus = [];
            if (productItem?.sKUs?.length > 0) {
                (productItem.sKUs || []).forEach((skuItem) => {
                    const sku = skuItem;
                    if (sku.partNumber) {
                        sku.partNumber = getBaseCode(sku.partNumber);
                    }
                    pSkus.push(sku);
                });
                productItem.sKUs = pSkus;
            }
            allProducts.push(productItem);
        });
        return allProducts;
    }
    return [];
};
